import { modals } from '@mantine/modals';
import { IconPhone } from '@tabler/icons-react';
import { useDebtor } from 'app/Private/Clients/Detail/client-atom';
import { useInvoiceSelection } from 'app/Private/Clients/Detail/UnhandledInvoicesTab/use-invoice-selection';
import { iconProps } from 'components/index';
import { tKeys, useTranslation } from 'locales';
import type { InvoiceAction, UnhandledInvoice } from 'modules/AtRisk/api/use-load-unhandled-client';
import { useCanSendMail } from 'modules/Mails/hooks/use-can-send-mail';
import { NewMail } from 'modules/Mails/pages/NewMail/NewMail';
import { isDefined, isEmpty } from 'remeda';
import type { ActionButtonDefinition } from '../use-build-action-buttons';
import { computeCommonProps } from './common';
import { DirectContactCall } from './DirectContactCall';
import { DirectContactChoice } from './DirectContactChoice';
import { openUnhandledInvoiceSelectionModal } from './open-unhandled-invoice-selection-modal';
import { usePauseWithNote } from './use-pause-with-note';

export function useDirectContact(): (
  action: InvoiceAction,
  invoices: Array<UnhandledInvoice>,
) => ActionButtonDefinition {
  const { t } = useTranslation();
  const { selectedInvoiceIds } = useInvoiceSelection();
  const debtor = useDebtor();
  const pauseWithNote = usePauseWithNote();
  const { canUseExternalMail } = useCanSendMail();

  const callDisabledReason = isEmpty(debtor.phones_attributes ?? [])
    ? t(tKeys.MODULES.AT_RISK.DIRECT_CONTACT.NO_PHONE)
    : undefined;
  const emailDisabledKey = computeEmailDisabledKey({
    hasEmail: !isEmpty(debtor.emails ?? []),
    canUseExternalMail,
  });
  const emailDisabledReason = isDefined(emailDisabledKey) ? t(emailDisabledKey) : undefined;

  return (action, invoices) => {
    const showCall = (invoiceIds: Array<string>) => {
      modals.closeAll();
      modals.open({
        title: t(tKeys.MODULES.AT_RISK.DIRECT_CONTACT.CALL),
        children: (
          <DirectContactCall
            phones={debtor.phones_attributes ?? []}
            onConfirm={() => pauseWithNote(invoiceIds)}
          />
        ),
      });
    };

    const showEmail = (invoiceIds: Array<string>) => {
      modals.closeAll();
      modals.open({
        size: 'xl',
        title: t(tKeys.MODULES.AT_RISK.DIRECT_CONTACT.EMAIL),
        children: (
          <NewMail
            debtorId={String(debtor.id)}
            debtorMail={debtor.emails?.[0]}
            onSent={() => pauseWithNote(invoiceIds)}
          />
        ),
      });
    };

    const handler = () =>
      openUnhandledInvoiceSelectionModal({
        title: t(tKeys.DEBTOR.REACTION.SELECT_INVOICES),
        selectedInvoiceIds,
        invoices,
        actionType: 'direct_contact',
        onNext: ({ invoiceIds }) => {
          modals.closeAll();
          modals.open({
            title: t(tKeys.MODULES.AT_RISK.DIRECT_CONTACT.TITLE),
            children: (
              <DirectContactChoice
                onCall={() => showCall(invoiceIds)}
                onEmail={() => showEmail(invoiceIds)}
                callDisabledReason={callDisabledReason}
                emailDisabledReason={emailDisabledReason}
              />
            ),
          });
        },
      });

    return {
      icon: <IconPhone {...iconProps()} />,
      onClick: handler,
      translationKey: tKeys.DIRECT_CONTACT,
      ...computeCommonProps(action, invoices),
    };
  };
}

type ComputeEmailDisabledKeyArgs = {
  hasEmail: boolean;
  canUseExternalMail: boolean;
};

// The client needs an address to write to, and the company needs a mailbox to send from.
function computeEmailDisabledKey({
  hasEmail,
  canUseExternalMail,
}: ComputeEmailDisabledKeyArgs): string | undefined {
  if (!hasEmail) return tKeys.MODULES.AT_RISK.DIRECT_CONTACT.NO_EMAIL;
  if (!canUseExternalMail) return tKeys.MODULES.AT_RISK.DIRECT_CONTACT.NO_MAILBOX;
  return undefined;
}
